import { useEntityStore } from '@ContractBuilder/store/entity.store';
import { ApiPatchOperation } from '@domain/schemas/api-patch-operation';
import { patchEntity } from '@mutations/patchEntity';
import { createBlock, updateBlock } from '@src/mutations';
import type {
  AddBlockFromLibraryToSubmissionPayload,
  AddBlockFromLibraryToTemplatePayload,
} from '@src/mutations/payloads';

import type { LibraryStoreSlice, MainStore, SliceType } from './types';

const addFromLibrary = async (
  get: () => MainStore,
  payload: AddBlockFromLibraryToSubmissionPayload | AddBlockFromLibraryToTemplatePayload,
): Promise<boolean> => {
  if (!get().submission) {
    return false;
  }

  useEntityStore.setState({ isLoading: true });
  try {
    const result = await patchEntity({ operation: ApiPatchOperation.AddNewBlockFromLibrary, ...payload });

    if (result?.message !== 'success') {
      return false;
    }

    await get().reloadSubmission();
    get().highlightChangedBlock(result.blockId);
    return true;
  } finally {
    useEntityStore.setState({ isLoading: false });
  }
};

export const createLibrarySlice: SliceType<LibraryStoreSlice> = (set, get) => ({
  createBlockInLibrary: async (payload) => {
    try {
      useEntityStore.setState({ isLoading: true });
      const { id } = await createBlock(payload);
      get().highlightChangedBlock(id);
      return id;
    } finally {
      set({ loadingBlockId: undefined });
      get().closeModal();
      useEntityStore.setState({ isLoading: false });
    }
  },
  restoreBlockInLibrary: async (blockId) => {
    if (!blockId) {
      return;
    }
    try {
      set({ loadingBlockId: blockId });
      useEntityStore.setState({ isLoading: true });
      await updateBlock(blockId, { operation: ApiPatchOperation.RestoreBlock });
      get().highlightChangedBlock(blockId);
    } finally {
      set({ loadingBlockId: undefined });
      get().closeModal();
      useEntityStore.setState({ isLoading: false });
    }
  },
  addBlockFromLibraryToSubmission: (payload) => addFromLibrary(get, payload),
  addBlockFromLibraryToTemplate: (payload) => addFromLibrary(get, payload),
  addBlocksFromLibrary: async ({ blockLibraryIds, sectionId, order }) => {
    const submission = get().submission;

    if (!submission || blockLibraryIds.length === 0) {
      return false;
    }

    useEntityStore.setState({ isLoading: true });
    try {
      const result = await patchEntity({
        operation: ApiPatchOperation.AddNewBlocksFromLibrary,
        blockLibraryIds,
        order,
        sectionId,
      });

      if (result?.message !== 'success') {
        return false;
      }

      await get().reloadSubmission();
      return true;
    } finally {
      useEntityStore.setState({ isLoading: false });
    }
  },
  updateBlockInLibrary: async (blockLibraryId, payload, originalBlockId) => {
    if (!blockLibraryId) {
      return;
    }

    try {
      const parsedId = originalBlockId ?? blockLibraryId;
      set({ loadingBlockId: parsedId });
      useEntityStore.setState({ isLoading: true });
      await updateBlock(blockLibraryId, payload);
      get().highlightChangedBlock(parsedId);
      const submission = get().submission;
      if (submission) {
        await get().reloadSubmission();
      }
    } finally {
      set({ loadingBlockId: undefined });
      get().closeModal();
      useEntityStore.setState({ isLoading: false });
    }
  },
  replaceBlockForBlockInLibrary: async (blockId, payload) => {
    const { blockLibraryId, sectionId, canDeleteOnTemplate, visibility, order } = payload;

    if (!blockId || !sectionId) {
      return;
    }

    // First delete the block (without reloading submission)
    await get().deleteBlock(blockId, false);

    // Then add the new block (this will reload submission)
    await get().addBlockFromLibraryToTemplate({
      blockLibraryId,
      canDeleteOnTemplate,
      order: order as number,
      sectionId,
      visibility,
    });
  },
  deactivateBlockInLibrary: async (blockId) => {
    if (!blockId) {
      return;
    }

    try {
      set({ loadingBlockId: blockId, isLoading: true });
      await updateBlock(blockId, { operation: ApiPatchOperation.DeactivateBlock });
      get().highlightChangedBlock(blockId);
    } finally {
      set({ loadingBlockId: undefined, isLoading: false });
    }
  },
  reactivateBlockInLibrary: async (blockId) => {
    if (!blockId) {
      return;
    }

    try {
      set({ loadingBlockId: blockId, isLoading: true });
      await updateBlock(blockId, { operation: ApiPatchOperation.ReactivateBlock });
      get().highlightChangedBlock(blockId);
    } finally {
      set({ loadingBlockId: undefined, isLoading: false });
    }
  },
});
